// Pure helpers for filtering the dealer roster by selected plant.
// Used by the Manufacturing → Plants right pane (dealers attached to
// the selected plant) and by the unattached pseudo-row bucket count.
//
// Kept pure (no React, no client calls) so the unit tests in
// tests/plantDealersFilter.test.ts can exercise edge cases without
// stubbing services.
import type { MfgPlant } from "@/lib/manufacturing/types";
import type {
  MfgDealerLocation,
  MfgSubscriberDealer,
  MfgSubscriberLocation,
} from "@/lib/manufacturing/dealerTypes";
import {
  isUnattachedSelection,
  type PlantSelection,
} from "./plantDealersDrafts";

export type DealerRosterCounts = {
  // Map of plant_id -> number of attached dealers (subscriber + non-app
  // combined). Used by the plants-list rows to render their "▸ N dealers"
  // chips.
  byPlantId: Map<string, number>;
  // How many dealers (subscriber + non-app) have no primary plant
  // assigned. Drives the Unattached pseudo-row visibility + label.
  unattached: number;
};

/**
 * Walk the full dealer roster once and tally per-plant attachment
 * counts plus the unattached bucket. Counts a subscriber dealer and a
 * non-app location as 1 each, even if a non-app location's
 * `linked_dealer_org_id` happens to point at one of the subscriber
 * dealers (that's the future-upgrade linkage, separate from supply
 * attachment — counting both gives the manufacturer the most honest
 * "rows I'd see if I drilled into this plant" estimate).
 *
 * Inactive subscriber dealers + inactive locations DO count — the
 * manufacturer still owns the linkage decision even when a dealer is
 * paused. The right-pane sections render the Inactive chips so the
 * user has full context.
 */
export function tallyDealerRosterCounts(
  subscriberDealers: MfgSubscriberDealer[],
  dealerLocations: MfgDealerLocation[]
): DealerRosterCounts {
  const byPlantId = new Map<string, number>();
  let unattached = 0;
  const bump = (id: string | null) => {
    if (!id) {
      unattached += 1;
      return;
    }
    byPlantId.set(id, (byPlantId.get(id) ?? 0) + 1);
  };
  for (const s of subscriberDealers) bump(s.primary_plant_id);
  for (const l of dealerLocations) bump(l.primary_plant_id);
  return { byPlantId, unattached };
}

/**
 * Filter a subscriber-dealer list to those attached to `selection`.
 * - Real plant id → primary_plant_id matches exactly.
 * - UNATTACHED sentinel → primary_plant_id is null.
 * - null selection → empty list (the right pane shouldn't be rendering
 *   anything when nothing is picked).
 */
export function filterSubscribersBySelection(
  list: MfgSubscriberDealer[],
  selection: PlantSelection
): MfgSubscriberDealer[] {
  if (selection === null) return [];
  if (isUnattachedSelection(selection)) {
    return list.filter((s) => s.primary_plant_id === null);
  }
  return list.filter((s) => s.primary_plant_id === selection);
}

/** Same as filterSubscribersBySelection, but for non-app locations. */
export function filterLocationsBySelection(
  list: MfgDealerLocation[],
  selection: PlantSelection
): MfgDealerLocation[] {
  if (selection === null) return [];
  if (isUnattachedSelection(selection)) {
    return list.filter((l) => l.primary_plant_id === null);
  }
  return list.filter((l) => l.primary_plant_id === selection);
}

/**
 * Returns subscriber dealers that are NOT currently attached to the
 * given plant — used by the "Attach existing dealer" picker. Includes
 * both unattached dealers and dealers attached to OTHER plants
 * (selecting one re-points its primary_plant_id at the active plant).
 *
 * `targetPlantId` must be a real plant id (the picker is only mounted
 * when a real plant is selected, never the unattached pseudo-row).
 */
export function subscribersAttachableToPlant(
  list: MfgSubscriberDealer[],
  targetPlantId: string
): MfgSubscriberDealer[] {
  return list.filter((s) => s.primary_plant_id !== targetPlantId);
}

/** Same as subscribersAttachableToPlant, but for non-app locations. */
export function locationsAttachableToPlant(
  list: MfgDealerLocation[],
  targetPlantId: string
): MfgDealerLocation[] {
  return list.filter((l) => l.primary_plant_id !== targetPlantId);
}

export type SubscriberLocationPlantCounts = {
  // plant_id -> number of app-using subscriber locations whose effective
  // plant resolves to that plant (direct overlay OR inheritance via the
  // subscriber-org default OR the manufacturer's default).
  byPlantId: Map<string, number>;
  // Locations with no direct overlay AND no org default AND no
  // manufacturer default — i.e. nothing in the chain catches them. Rare
  // in practice once a manufacturer has set a default plant.
  unassigned: number;
};

/**
 * Tally app-using subscriber locations by their effective plant.
 *
 * Effective plant chain (mirrors
 * dealerLocationPlantResolver.resolveSubscriberLocationEffectivePlant):
 *   1. loc.primary_plant_id  (mfg_subscriber_location_settings overlay)
 *   2. dealerOrgPlantId.get(loc.subscriber_org_id) — the subscriber-org's
 *      manufacturer-side default (mfg_dealer_org_settings.primary_plant_id)
 *   3. defaultPlant?.id — the manufacturer's org default plant
 *   4. unassigned bucket
 *
 * Unlike the dealer-org / non-app tally, this one always counts each
 * location exactly once and never against a stale plant id (we skip the
 * direct overlay if its plant id isn't in `plantById`, which would only
 * happen if the underlying plant row was hard-deleted out from under
 * the overlay — falls back through inheritance).
 *
 * Kept pure so the Plants-list per-row "📍 N sub-loc" chip and any
 * future plant-capacity displays read off the same source of truth as
 * the slice-5 Dealer-locations slider.
 */
export function tallySubscriberLocationsByEffectivePlant(
  subscriberLocations: ReadonlyArray<MfgSubscriberLocation>,
  dealerOrgPlantId: Map<string, string>,
  defaultPlant: MfgPlant | null,
  plantById: Map<string, MfgPlant>
): SubscriberLocationPlantCounts {
  const byPlantId = new Map<string, number>();
  let unassigned = 0;
  const bump = (id: string) => {
    byPlantId.set(id, (byPlantId.get(id) ?? 0) + 1);
  };
  for (const loc of subscriberLocations) {
    if (loc.primary_plant_id && plantById.has(loc.primary_plant_id)) {
      bump(loc.primary_plant_id);
      continue;
    }
    const orgPid = dealerOrgPlantId.get(loc.subscriber_org_id);
    if (orgPid && plantById.has(orgPid)) {
      bump(orgPid);
      continue;
    }
    if (defaultPlant) {
      bump(defaultPlant.id);
      continue;
    }
    unassigned += 1;
  }
  return { byPlantId, unassigned };
}
